import React, { useState , useEffect } from "react";
import { 
  TextField, 
  Button, 
  Dialog, 
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  IconButton
} from "@mui/material";
import { AppCTX } from '../data/AppContext';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ReceiptIcon from '@mui/icons-material/Receipt';
import NotesIcon from '@mui/icons-material/Notes';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';

// Styled components for better visual design
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    backgroundColor: '#1a1a1a',
    color: '#ffffff',
    minWidth: 320,
    maxWidth: 420,
    width: '95vw',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: '#222222',
  color: '#ffffff',
  borderBottom: '1px solid #333333',
  padding: '16px 16px 8px 16px',
  '& .MuiTypography-root': {
    fontSize: '1.1rem',
    fontWeight: 600,
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: '16px',
  backgroundColor: '#1a1a1a',
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  backgroundColor: '#222222',
  borderTop: '1px solid #333333',
  padding: '12px 16px',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#2a2a2a',
    borderRadius: 8,
    '& fieldset': {
      borderColor: '#444444',
    },
    '&:hover fieldset': {
      borderColor: '#666666',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1976d2',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#cccccc',
  },
  '& .MuiInputBase-input': {
    color: '#ffffff',
  },
  '& .MuiFormHelperText-root': {
    color: '#ff6b6b',
  },
}));

const StyledButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 24px',
  fontSize: '1rem',
  ...(variant === 'contained' && {
    backgroundColor: '#1976d2',
    '&:hover': {
      backgroundColor: '#1565c0',
    },
  }),
  ...(variant === 'outlined' && {
    borderColor: '#666666',
    color: '#ffffff',
    '&:hover': {
      borderColor: '#888888',
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  }),
}));

const UploadButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  padding: '16px 24px',
  backgroundColor: '#2a2a2a',
  border: '2px dashed #666666',
  color: '#ffffff',
  '&:hover': {
    backgroundColor: '#333333',
    borderColor: '#888888',
  },
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const steps = [
  { label: 'Homeowner(s)', icon: <PersonAddIcon /> },
  { label: 'Utility', icon: <ReceiptIcon /> },
  { label: 'Notes', icon: <NotesIcon /> },
];

function AddProspect() {
  const { 
    addProspect, 
    user, 
    openAddProspect, 
    setOpenAddProspect, 
    newProspect, 
    setNewProspect,
    addAddress,
    setStatus,
    useApiClient
  } = React.useContext(AppCTX);
  const apiClient = useApiClient();

  // Homeowner fields
  const [owners, setOwners] = useState([
    { name: '', number: '', nameError: '', numberError: '' }
  ]);
  const [notes, setNotes] = useState('');
  const [notesError, setNotesError] = useState('');
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (!openAddProspect && newProspect.coords != null) {
      setNewProspect({
        owners: [],
        notes: [],
        utilityBill: null,
        coords: false,
        address: '',
        setter: '',
        closer: '',
        systemInstalled: false,
        appointmentStart: null,
        appointmentSet: false,
      });
      setOwners([{ name: '', number: '', nameError: '', numberError: '' }]);
      setNotes('');
      setNotesError('');
      setStep(1);
    } else if (newProspect.coords) {
      addAddress();
    }
  }, [openAddProspect]);

  // Pre-populate owners from CoreLogic data when modal opens
  useEffect(() => {
    if (openAddProspect && newProspect.owners && newProspect.owners.length > 0) {
      console.log('[AddProspect] Pre-populating owners from CoreLogic data:', newProspect.owners);
      
      // Convert CoreLogic owners to form format
      const formOwners = newProspect.owners.map(owner => ({
        name: owner.name || '',
        number: owner.number || '',
        nameError: '',
        numberError: ''
      }));
      
      // Ensure we have at least one owner field
      if (formOwners.length === 0) {
        formOwners.push({ name: '', number: '', nameError: '', numberError: '' });
      }
      
      // Limit to 2 owners (as per form design)
      const limitedOwners = formOwners.slice(0, 2);
      
      setOwners(limitedOwners);
      console.log('[AddProspect] Pre-populated owners:', limitedOwners);
    }
  }, [openAddProspect, newProspect.owners]);

  // Handlers for owner fields
  const handleOwnerChange = (idx, field, value) => {
    setOwners(prev => prev.map((owner, i) =>
      i === idx ? { ...owner, [field]: value, [`${field}Error`]: '' } : owner
    ));
  };

  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    // Always start with '+1 '
    let digits = value.replace(/\D/g, '');
    if (!digits.startsWith('1')) {
      digits = '1' + digits;
    }
    // Remove leading 1 for formatting
    const phone = digits.slice(1, 11);
    let formatted = '+1 ';
    if (phone.length > 0) {
      formatted += '(' + phone.slice(0, 3);
    }
    if (phone.length >= 3) {
      formatted += ') ' + phone.slice(3, 6);
    }
    if (phone.length >= 6) {
      formatted += '-' + phone.slice(6, 10);
    }
    return formatted.trim();
  };

  const handlePhoneChange = (idx, value) => {
    const formatted = formatPhoneNumber(value);
    handleOwnerChange(idx, 'number', formatted);
  };

  const addOwnerField = () => {
    if (owners.length < 2) {
      setOwners([...owners, { name: '', number: '', nameError: '', numberError: '' }]);
    }
  };

  const removeOwnerField = (idx) => {
    if (owners.length > 1) {
      setOwners(owners.filter((_, i) => i !== idx));
    }
  };

  const validateOwners = () => {
    let valid = true;
    setOwners(prev => prev.map(owner => {
      let nameError = '', numberError = '';
      if (!owner.name.trim()) {
        nameError = 'Enter name';
        valid = false;
      }
      // Check if phone number is complete (should be +1 (xxx) xxx-xxxx format)
      const phoneDigits = owner.number.replace(/\D/g, '');
      if (phoneDigits.length !== 11 || !owner.number.match(/^\+1 \(\d{3}\) \d{3}-\d{4}$/)) {
        numberError = 'Enter a complete phone number';
        valid = false;
      }
      return { ...owner, nameError, numberError };
    }));
    return valid;
  };

  const uploadUtilityBill = async (e) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      // 1) get presigned URL
      const presign = await apiClient.post('/api/uploads/utility-bill', {
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
      });
      if (!presign.success) {
        throw new Error(presign.error || 'Failed to get upload URL');
      }
      // 2) upload to S3
      await axios.put(presign.uploadUrl, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      // 3) save file URL/key to prospect
      const url = presign.fileUrl || presign.key;
      const update = { ...newProspect, utilityBill: url };
      setNewProspect(update);
      setStep(3);
      setStatus({ notificationType: 'success', message: 'Utility bill uploaded' });
    } catch (err) {
      console.error(err);
      setStatus({ notificationType: 'error', message: 'Upload failed' });
    }
  };

  const handleNext = () => {
    if (step === 1) {
      if (validateOwners()) {
        let update = {
          ...newProspect,
          coords: newProspect.coords ? {
            lat: newProspect.coords.lat,
            lng: newProspect.coords.lng
          } : undefined,
          owners: owners.map(({ name, number }) => ({ name, number }))
        };
        setNewProspect(update);
        setStep(2);
      }
    } else if (step === 2) {
      setStep(3);
    } else if (step === 3) {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    if (notes.length === 0) {
      setNotesError('Enter some notes');
      return;
    }
    let payload = {
      ...newProspect,
      coords: newProspect.coords ? {
        lat: newProspect.coords.lat,
        lng: newProspect.coords.lng
      } : undefined,
      owners: owners.map(({ name, number }) => ({ name, number })),
      notes: [`${user.data.name} - ${notes}`],
      setter: user.data.name
    };
    addProspect(payload);
    setOpenAddProspect(false);
  };

  // Render step content
  const renderStepContent = () => {
    if (step === 1) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, color: '#cccccc' }}>
            Enter homeowner information (add up to 2)
          </Typography>
          <Stack spacing={2}>
            {owners.map((owner, idx) => (
              <Box key={idx} sx={{ mb: 1 }}>
                <StyledTextField
                  required
                  fullWidth
                  label={`Full Name${owners.length > 1 ? ` ${idx + 1}` : ''}`}
                  value={owner.name}
                  onChange={e => handleOwnerChange(idx, 'name', e.target.value)}
                  error={!!owner.nameError}
                  helperText={owner.nameError}
                  placeholder="Enter homeowner's full name"
                  sx={{ mb: 1 }}
                />
                <StyledTextField
                  required
                  fullWidth
                  label={`Phone Number${owners.length > 1 ? ` ${idx + 1}` : ''}`}
                  value={owner.number}
                  onChange={e => handlePhoneChange(idx, e.target.value)}
                  error={!!owner.numberError}
                  helperText={owner.numberError}
                  placeholder="+1 (555) 123-4567"
                  inputProps={{
                    maxLength: 17 // +1 (xxx) xxx-xxxx = 17 characters
                  }}
                />
                {owners.length > 1 && idx > 0 && (
                  <IconButton size="small" onClick={() => removeOwnerField(idx)} aria-label="Remove owner" sx={{ color: '#ff6b6b', mt: 0.5 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
            {owners.length < 2 && (
              <StyledButton onClick={addOwnerField} variant="outlined" startIcon={<PersonAddIcon />} fullWidth>
                Add Another Homeowner
              </StyledButton>
            )}
          </Stack>
        </Box>
      );
    } else if (step === 2) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, color: '#cccccc' }}>
            Upload a recent utility bill to help with solar calculations
          </Typography>
          <Stack spacing={2}>
            <UploadButton 
              component="label" 
              variant="outlined" 
              startIcon={<CloudUploadIcon />}
              fullWidth
            >
              <Box sx={{ textAlign: 'center' }}>
                <CloudUploadIcon sx={{ fontSize: 36, mb: 1, color: '#666666' }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Upload Utility Bill
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click to select or drag and drop
                </Typography>
              </Box>
              <VisuallyHiddenInput onChange={uploadUtilityBill} type="file" accept="image/*,.pdf" />
            </UploadButton>
            <StyledButton 
              onClick={() => setStep(3)} 
              variant="outlined"
              fullWidth
            >
              Skip for now
            </StyledButton>
          </Stack>
        </Box>
      );
    } else if (step === 3) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, color: '#cccccc' }}>
            Add any relevant notes about this prospect
          </Typography>
          <Stack spacing={2}>
            <StyledTextField
              fullWidth
              label="Notes"
              value={notes}
              onChange={e => { setNotes(e.target.value); setNotesError(''); }}
              multiline
              minRows={4}
              error={notesError.length > 0}
              helperText={notesError}
              placeholder="Enter any relevant notes about this prospect..."
            />
            <StyledButton 
              onClick={handleSubmit} 
              variant="contained"
              fullWidth
              startIcon={<PersonAddIcon />}
            >
              Save Prospect
            </StyledButton>
          </Stack>
        </Box>
      );
    }
  };

  return (
    <StyledDialog
      open={openAddProspect}
      onClose={() => setOpenAddProspect(false)}
      maxWidth="sm"
      fullWidth
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {steps[step - 1].label}
          </Typography>
          <IconButton
            onClick={() => setOpenAddProspect(false)}
            sx={{ color: '#ffffff' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Stepper activeStep={step - 1} alternativeLabel sx={{ mt: 1, mb: 0, width: '100%' }}>
          {steps.map((stepItem, index) => (
            <Step key={index}>
              <StepLabel 
                icon={stepItem.icon}
                sx={{
                  '& .MuiStepLabel-label': {
                    color: '#cccccc',
                    fontSize: '0.8rem',
                  },
                  '& .MuiStepIcon-root': {
                    color: index < step - 1 ? '#1976d2' : '#666666',
                  },
                }}
              >
                {stepItem.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </StyledDialogTitle>
      <StyledDialogContent>
        {renderStepContent()}
      </StyledDialogContent>
      <StyledDialogActions>
        <StyledButton 
          onClick={() => setOpenAddProspect(false)} 
          variant="outlined"
        >
          Cancel
        </StyledButton>
        {step < 3 && (
          <StyledButton 
            onClick={handleNext} 
            variant="contained"
          >
            Next
          </StyledButton>
        )}
      </StyledDialogActions>
    </StyledDialog>
  );
}

export default AddProspect;
